import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Chip } from '@mui/material';
import { adminApi } from '../../api';

const fmtBytes = (b) => {
  if (!b) return '0 B';
  const units = ['B','KB','MB','GB','TB'];
  let i = 0; let v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const TierPill = ({ label, count, color, bg }) => (
  <Chip label={`${label}: ${count ?? 0}`} size="small"
    sx={{ bgcolor: bg, color, borderRadius: 0, fontSize: 11, height: 20, mr: 0.5 }} />
);

const StorageStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getFacilityStats().then(setStats).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const total = stats.reduce((a, f) => ({
    studies: (a.studies || 0) + (f.study_count || 0),
    instances: (a.instances || 0) + (f.total_instances || 0),
    bytes: (a.bytes || 0) + (f.total_bytes || 0),
    hot: (a.hot || 0) + (f.hot_count || 0),
    cool: (a.cool || 0) + (f.cool_count || 0),
    archive: (a.archive || 0) + (f.archive_count || 0),
  }), {});

  return (
    <Box>
      <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1, mb: 3 }}>
        Storage
      </Typography>

      {/* Totals */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          ['Total Studies', total.studies?.toLocaleString()],
          ['Total Instances', total.instances?.toLocaleString()],
          ['Total Size', fmtBytes(total.bytes)],
        ].map(([label, value]) => (
          <Paper key={label} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2, borderRadius: 1, minWidth: 160 }}>
            <Typography sx={{ color: '#555', fontSize: 11, letterSpacing: 1 }}>{label.toUpperCase()}</Typography>
            <Typography sx={{ color: 'primary.main', fontSize: 26, fontWeight: 700, fontFamily: 'monospace' }}>{value ?? '—'}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Per facility */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {stats.map((f, i) => (
          <Paper key={i} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1, p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ color: '#e0e0e0', fontSize: 14, fontWeight: 600 }}>{f.facility_name}</Typography>
              <Box>
                <TierPill label="Hot" count={f.hot_count} color="#00bcd4" bg="#002233" />
                <TierPill label="Cool" count={f.cool_count} color="#7986cb" bg="#1a1a2e" />
                <TierPill label="Archive" count={f.archive_count} color="#ff8a65" bg="#2e1a0e" />
                {f.pending_delete_count > 0 && (
                  <TierPill label="Pending Delete" count={f.pending_delete_count} color="#ef9a9a" bg="#2e0e0e" />
                )}
              </Box>
            </Box>

            {/* Storage bar */}
            {(() => {
              const total = (f.hot_count || 0) + (f.cool_count || 0) + (f.archive_count || 0) || 1;
              return (
                <Box sx={{ height: 6, display: 'flex', borderRadius: 1, overflow: 'hidden', bgcolor: '#1a1a1a', mb: 1.5 }}>
                  <Box sx={{ width: `${f.hot_count / total * 100}%`, bgcolor: '#00bcd4' }} />
                  <Box sx={{ width: `${f.cool_count / total * 100}%`, bgcolor: '#7986cb' }} />
                  <Box sx={{ width: `${f.archive_count / total * 100}%`, bgcolor: '#ff8a65' }} />
                </Box>
              );
            })()}

            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                ['Studies', f.study_count?.toLocaleString()],
                ['Patients', f.patient_count?.toLocaleString()],
                ['Instances', f.total_instances?.toLocaleString()],
                ['Size', fmtBytes(f.total_bytes)],
                ['Oldest', fmtDate(f.oldest_study)],
                ['Newest', fmtDate(f.newest_study)],
                ['Last Received', fmtDate(f.last_received)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ color: '#444', fontSize: 10, letterSpacing: 0.8 }}>{label.toUpperCase()}</Typography>
                  <Typography sx={{ color: '#aaa', fontSize: 13, fontFamily: 'monospace' }}>{value ?? '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default StorageStats;
